import React, { memo, useState } from 'react'
import moment from 'moment-timezone'
import { Avatar } from 'transfix-ui/components/Avatar'
import { IconButton } from 'transfix-ui/components/IconButton'
import { OverflowMenu } from 'transfix-ui/components/OverflowMenu'
import { TableRow, TableCell } from 'transfix-ui/components/Table'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import Box from 'transfix-ui/core/Box'
import {
  RequestForProposal,
  useGetWorkStatus,
  GetWorkStatusResponse,
  ShipperApplication,
} from 'apps/marketplace/apiTypes/requestForProposals'
import { SALESFORCE_URL_PREFIX } from 'apps/marketplace/utils/constants/salesforceUrl'
import { EditIcon, DeleteIcon, EllipsisIcon } from 'transfix-icons'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import { StatusTag } from './StatusTag'

interface IRequestForProposalsTableRowProps {
  requestForProposal: RequestForProposal
  openDeleteConfirmation: (id: number) => void
  openEditConfirmation: (
    id: number,
    salesforceRfpId: string,
    rfpType: string,
    shipperApplication: ShipperApplication
  ) => void
}

export const RequestForProposalsTableRow = memo<IRequestForProposalsTableRowProps>(
  ({ openDeleteConfirmation, openEditConfirmation, requestForProposal }) => {
    const {
      createdAt,
      createdByUser,
      googleSheetsUrl,
      id,
      rfpType,
      salesforceRfpId,
      salesforceRfpTitle,
      shipperApplication,
      status,
    } = requestForProposal
    const { name: shipperName } = shipperApplication
    const { dateFormatShort } = FORMATS
    const [isExpanded, setIsExpanded] = useState(false)
    const { data: workStatus, error, loading, refetch } = useGetWorkStatus({
      queryParams: {
        id,
      },
      lazy: true,
    })

    const { firstName, lastName } = createdByUser
    const userName = firstName || lastName ? `${firstName} ${lastName}` : null

    const formattedRfpTypes = {
      rfp: 'RFP',
      reprice: 'Reprice',
      data_analysis: 'Data Analysis',
      shipper_rate_index: 'Shipper Rate Index',
    } as {
      [key: string]: string
    }

    const overflowOptions = [
      ...(status === 'success' || status === null
        ? [
            {
              itemComponent: (
                <div className='flex flex-row items-center'>
                  <EditIcon size='large' />
                  <span className='ml2'>Edit</span>
                </div>
              ),
              label: 'edit',
              action: () => {
                openEditConfirmation(id, salesforceRfpId, rfpType, shipperApplication)
              },
            },
          ]
        : []),
      {
        itemComponent: (
          <div className='flex flex-row items-center'>
            <DeleteIcon size='large' />
            <span className='ml2'>Delete</span>
          </div>
        ),
        label: 'delete',
        action: () => {
          openDeleteConfirmation(id)
        },
      },
    ]

    const formatWorkStatus = (statusData: GetWorkStatusResponse | null): string => {
      if (statusData?.total === null) return 'No data'
      return `${statusData?.success || 0} of ${statusData?.total} lanes normed`
    }

    return (
      <TableRow dataTest={`rfp-${id}`} highlightOnHover>
        <TableCell dataTest={`rfp-${id}-id`}>{id || DISPLAY.empty}</TableCell>
        <TableCell dataTest={`rfp-${id}-createdAt`}>
          {createdAt ? moment(createdAt).format(dateFormatShort) : DISPLAY.empty}
        </TableCell>
        <TableCell dataTest={`rfp-${id}-rfpType`}>
          {formattedRfpTypes[rfpType] || DISPLAY.empty}
        </TableCell>
        <TableCell dataTest={`rfp-${id}-salesforceRfpTitle`}>
          {salesforceRfpTitle || DISPLAY.empty}
        </TableCell>
        <TableCell dataTest={`rfp-${id}-shipper`}>{shipperName || DISPLAY.empty}</TableCell>
        <TableCell dataTest={`rfp-${id}-googleSheetsUrl`}>
          <a href={googleSheetsUrl} target='_blank' rel='noopener noreferrer'>
            Google Sheets URL
          </a>
        </TableCell>
        <TableCell dataTest={`rfp-${id}-salesforceRfpId`}>
          {salesforceRfpId ? (
            <a
              href={`${SALESFORCE_URL_PREFIX}${salesforceRfpId}/view`}
              target='_blank'
              rel='noopener noreferrer'>
              {salesforceRfpId}
            </a>
          ) : (
            DISPLAY.empty
          )}
        </TableCell>
        <TableCell dataTest={`rfp-${id}-status`}>
          {status === 'pending' || status === 'error' ? (
            <Tooltip
              position='bottom'
              trigger={
                <span onMouseEnter={() => refetch()}>
                  <StatusTag status={status} />
                </span>
              }>
              {loading ? 'Checking status...' : error?.message || formatWorkStatus(workStatus)}
            </Tooltip>
          ) : (
            <StatusTag status={status} />
          )}
        </TableCell>
        <TableCell dataTest={`rfp-${id}-creator`}>
          {userName ? (
            <Box display='flex' alignItems='center'>
              <Box mr={1}>
                <Avatar name={userName} />
              </Box>
              <Box flexGrow={1}>{userName}</Box>
            </Box>
          ) : (
            DISPLAY.empty
          )}
        </TableCell>
        <TableCell dataTest={`rfp-${id}-delete`}>
          <OverflowMenu
            onExpanded={setIsExpanded}
            expanded={isExpanded}
            dropdownId={`${id}-overflow`}
            trigger={
              <IconButton
                Icon={EllipsisIcon}
                label='edit'
                data-test='edit-row'
                onClick={() => setIsExpanded(true)}
                size='large'
              />
            }
            options={overflowOptions}
            dataTest='sourced-rate-dropdown'
          />
        </TableCell>
      </TableRow>
    )
  }
)
